import React from 'react';

import { ModalView } from '@knocklabs/react';

import { BodyRow, CardImage, EdgeToEdgeImage, InlineHelpLink, OnImageCloseButton } from '../styles';
import { GuideActions, GuideHelpLink } from './shared';
import { ImageContent, ModalContent } from './types';
import { isValidHttpUrl, useVatixGuide } from './useVatixGuide';

// `leading` is the bleeding image_top image; `inset` is the non-bleed image_top_inset one.
const ModalImage: React.FC<{
  content: ModalContent;
  leading?: boolean;
  inset?: boolean;
  onDismiss: () => void;
  onImageClick: (e: React.MouseEvent, image: ImageContent) => void;
}> = ({ content, leading, inset, onDismiss, onImageClick }) => {
  if (!content.image?.url) {
    return null;
  }

  const img = (
    <ModalView.Img
      src={content.image.url}
      alt={content.image.alt}
      onClick={(e) => content.image && onImageClick(e, content.image)}
    />
  );
  const href = isValidHttpUrl(content.image.action) ? content.image.action : undefined;

  if (inset) {
    return (
      <CardImage href={href} target="_blank" rel="noreferrer">
        {img}
      </CardImage>
    );
  }

  return (
    <EdgeToEdgeImage href={href} target="_blank" rel="noreferrer" $leading={leading}>
      {img}
      {leading && content.dismissible && (
        <OnImageCloseButton>
          <ModalView.Close
            onClick={(e) => {
              // Stop the click reaching the image's own link so dismissing doesn't also navigate.
              e.preventDefault();
              e.stopPropagation();
              onDismiss();
            }}
          />
        </OnImageCloseButton>
      )}
    </EdgeToEdgeImage>
  );
};

export const VatixModal: React.FC<{ guideKey?: string }> = ({ guideKey }) => {
  const { guide, step, colorMode, onDismiss, onButtonClick, onImageClick } = useVatixGuide<ModalContent>(
    'modal',
    guideKey
  );

  if (!guide || !step) {
    return null;
  }

  const content = step.content;
  const imageLeading = content.layout === 'image_top';
  const imageInsetLeading = content.layout === 'image_top_inset';

  const actions = <GuideActions view={ModalView} content={content} withDivider onButtonClick={onButtonClick} />;

  // Title sits below the header row, not inline with the headline, matching the card.
  const header = (
    <>
      <div className="knock-guide-modal__header">
        <div className="knock-guide-modal__headline">{content.headline}</div>
        {!imageLeading && content.dismissible && <ModalView.Close onClick={onDismiss} />}
      </div>
      <ModalView.Title title={content.title} />
    </>
  );

  const bodyRow = (
    <BodyRow>
      <ModalView.Body body={content.body} />
      <GuideHelpLink helpLink={content.help_link} as={InlineHelpLink} />
    </BodyRow>
  );

  return (
    <ModalView.Root onOpenChange={(open) => !open && onDismiss()}>
      <ModalView.Overlay />
      <ModalView.Content data-knock-color-mode={colorMode}>
        {imageLeading ? (
          <>
            <ModalImage content={content} leading onDismiss={onDismiss} onImageClick={onImageClick} />
            {header}
            {bodyRow}
            {actions}
          </>
        ) : imageInsetLeading ? (
          <>
            {header}
            <ModalImage content={content} inset onDismiss={onDismiss} onImageClick={onImageClick} />
            {bodyRow}
            {actions}
          </>
        ) : (
          <>
            {header}
            {bodyRow}
            <ModalImage content={content} onDismiss={onDismiss} onImageClick={onImageClick} />
            {actions}
          </>
        )}
      </ModalView.Content>
    </ModalView.Root>
  );
};
